import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {assert} from 'assert-ts';
import type {ViewMode} from 'services/advancedSearch/types';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import type {AdvancedSearchContextType} from '../types';
import {
  useAvailableFiltersWithLabel,
  useCurrentColumns,
  useCurrentFilters,
  useDataFetch,
  useGetMetadataIds,
  useSort,
  useViewMode,
} from '../hooks';
import {mapDataToMetadataId} from '../mappers';

export const AdvancedSearchContext = createContext<
  AdvancedSearchContextType | undefined
>(undefined);

export const useAdvancedSearchContextProviderValue =
  (): AdvancedSearchContextType => {
    const {statusAvailableFilters, availableFilters} =
      useAvailableFiltersWithLabel();

    const {
      currentFilters,
      addCurrentFilter,
      setCurrentFilters,
      updateCurrentFilter,
      removeCurrentFilterById,
    } = useCurrentFilters();

    const {
      loadingColumns,
      allColumns,
      currentColumns,
      schema,
      setCurrentColumns,
      addCurrentColumn,
      removeCurrentColumn,
    } = useCurrentColumns();

    const {paginationModel, resetPaginationModel} =
      useColumnConfigState('advancedSearch');

    const {onSearch, onDownload, dataResponse, loadingSearch, loadingDownload} =
      useDataFetch(allColumns);

    const {viewMode, setViewMode} = useViewMode({
      currentColumns,
      addCurrentColumn,
    });

    const getMetadataIds = useGetMetadataIds();

    const {sort} = useSort();

    const loading = useMemo(() => {
      return (
        loadingColumns || [statusAvailableFilters].some(s => s === 'Loading')
      );
    }, [loadingColumns, statusAvailableFilters]);

    const handleOnDownload = useCallback(
      (
        viewMode: ViewMode,
        columns: string[] | undefined,
        distinctOn?: string,
      ) => onDownload(viewMode, currentFilters, columns, distinctOn),
      [currentFilters, onDownload],
    );

    const handleOnSearch = useCallback(
      (resetPage?: boolean) => {
        if (resetPage) {
          resetPaginationModel();
        }

        onSearch({
          type: viewMode,
          filters: currentFilters,
          columns: currentColumns,
          sort,
          page: resetPage ? 0 : paginationModel.page,
          size: paginationModel.pageSize,
        });
      },
      [
        onSearch,
        viewMode,
        currentFilters,
        currentColumns,
        sort,
        paginationModel.page,
        paginationModel.pageSize,
        resetPaginationModel,
      ],
    );

    const handleGetMetadataIdsPage = useCallback(
      (page: number, size: number) => {
        return getMetadataIds(viewMode, currentFilters, sort, page, size);
      },
      [getMetadataIds, viewMode, currentFilters, sort],
    );

    const handleGetCurrentMetadataIds = useCallback(() => {
      return dataResponse.rows.map(mapDataToMetadataId);
    }, [dataResponse]);

    useEffect(() => {
      onSearch({
        type: viewMode,
        filters: currentFilters,
        columns: currentColumns,
        sort,
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });
      // Don't search on every filter change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      viewMode,
      currentColumns,
      sort,
      paginationModel.page,
      paginationModel.pageSize,
    ]);

    return useMemo(
      () => ({
        loading,
        availableFilters,
        currentFilters,
        addCurrentFilter,
        setCurrentFilters,
        updateCurrentFilter,
        removeCurrentFilterById,
        onSearch: handleOnSearch,
        onDownload: handleOnDownload,
        allColumns: allColumns,
        currentColumns,
        loadingSearch,
        loadingDownload,
        schema,
        setCurrentColumns,
        addCurrentColumn,
        removeCurrentColumn,
        viewMode,
        setViewMode,
        getMetadataIdsPage: handleGetMetadataIdsPage,
        getCurrentMetadataIds: handleGetCurrentMetadataIds,
        dataResponse,
      }),
      [
        loading,
        availableFilters,
        currentFilters,
        addCurrentFilter,
        setCurrentFilters,
        updateCurrentFilter,
        removeCurrentFilterById,
        handleOnSearch,
        handleOnDownload,
        allColumns,
        currentColumns,
        loadingSearch,
        loadingDownload,
        schema,
        setCurrentColumns,
        addCurrentColumn,
        removeCurrentColumn,
        viewMode,
        setViewMode,
        handleGetMetadataIdsPage,
        handleGetCurrentMetadataIds,
        dataResponse,
      ],
    );
  };

export const useAdvancedSearchContext = (): AdvancedSearchContextType => {
  return assert(
    useContext(AdvancedSearchContext),
    'AdvancedSearchContext: context expected',
  );
};
